import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { iconShield } from '#src/atoms/icons/system/iconShield.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';

import { BottomActions } from '../AccountVerification/shared/BottomActions.tsx';
import { UserAvatarWithFlag } from '../AccountVerification/shared/UserAvatarWithFlag.tsx';

const Root = styled(Vertical).attrs({ gap: 4 })`
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--c-black);
  overflow: hidden;
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  padding: var(--quadruple-unit) var(--double-unit) 0;
  gap: var(--double-unit);
`;

const AvatarPair = styled(Horizontal).attrs({ gap: 1.5, center: true })`
  width: 100%;
`;

const Dots = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--c-nd-400);
  }
`;

const ProviderCircle = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--c-blue-400);
  color: var(--c-white);
  font-size: 28px;
  flex-shrink: 0;
`;

const ClockBadge = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--c-white);
  color: var(--c-blue-400);
  font-size: 12px;
  border: 2px solid var(--c-black);
`;

const TextContent = styled(Vertical)`
  width: 100%;
  align-items: center;
  text-align: center;
  gap: 0;
`;

const Title = styled(HeadlineL)`
  color: var(--c-white);
  width: 100%;
  margin-bottom: var(--unit);
`;

const Description = styled(BodyM)`
  color: var(--c-nd-600);
  width: 100%;
`;

type Props = {
  onDone: () => void;
};

export const JonumVerificationInProgress = ({ onDone }: Props) => (
  <Root>
    <Content>
      <AvatarPair>
        <UserAvatarWithFlag />
        <Dots>
          <span />
          <span />
          <span />
        </Dots>
        <ProviderCircle>
          <FontAwesomeIcon icon={iconShield} />
          <ClockBadge>
            <FontAwesomeIcon icon={iconClock} />
          </ClockBadge>
        </ProviderCircle>
      </AvatarPair>
      <TextContent>
        <Title bold>
          <FormattedMessage defaultMessage="Verification in progress!" />
        </Title>
        <Description>
          <FormattedMessage defaultMessage="It usually takes 5 to 10 minutes, sometimes up to 24 hours." />
        </Description>
        <Description>
          <FormattedMessage defaultMessage="We'll notify you as soon as it's validated." />
        </Description>
      </TextContent>
    </Content>
    <BottomActions
      primaryAction={
        <Button size="medium" color="primary" onClick={onDone} fullWidth>
          <FormattedMessage defaultMessage="Complete the verification" />
        </Button>
      }
    />
  </Root>
);
